<template>
  <section class="about-story-cards" :style="sectionStyle" aria-label="About StoReel">
    <article
      v-for="(card, index) in cards"
      :key="card.description"
      class="about-story-card"
      :style="cardStyle(index)"
    >
      <img
        class="about-story-card__logo"
        :src="card.logo"
        alt="StoReel"
        loading="lazy"
        decoding="async"
        draggable="false"
        :style="logoStyle"
      />
      <p class="about-story-card__copy" :style="copyStyle(card.textBox)">
        {{ card.description }}
      </p>
    </article>
  </section>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import type { CSSProperties } from 'vue'

import type { AboutStoryCard, DesignBox } from '../../data/home2026'

const CARD_WIDTH = 500
const CARD_HEIGHT = 550
const CARD_GAP = 10
const LOGO_BOX: DesignBox = { x: 30, y: 50, w: 150, h: 24 }

const props = defineProps<{
  box: DesignBox
  cards: AboutStoryCard[]
  scaledPx: (value: number) => string
}>()

const boxStyle = (box: DesignBox): CSSProperties => ({
  left: props.scaledPx(box.x),
  top: props.scaledPx(box.y),
  width: props.scaledPx(box.w),
  height: props.scaledPx(box.h),
})

const relativeBoxStyle = (box: DesignBox): CSSProperties => ({
  left: props.scaledPx(box.x),
  top: props.scaledPx(box.y),
  width: props.scaledPx(box.w),
  minHeight: props.scaledPx(box.h),
})

const sectionStyle = computed<CSSProperties>(() => boxStyle(props.box))

const logoStyle = computed<CSSProperties>(() => boxStyle(LOGO_BOX))

const cardStyle = (index: number): CSSProperties => ({
  left: props.scaledPx(index * (CARD_WIDTH + CARD_GAP)),
  top: '0px',
  width: props.scaledPx(CARD_WIDTH),
  height: props.scaledPx(CARD_HEIGHT),
  borderRadius: props.scaledPx(30),
})

const copyStyle = (box: DesignBox): CSSProperties => ({
  ...relativeBoxStyle(box),
  fontSize: props.scaledPx(30),
  lineHeight: props.scaledPx(42),
})
</script>

<style scoped>
.about-story-cards {
  position: absolute;
  z-index: 3;
  overflow: visible;
  pointer-events: none;
}

.about-story-card {
  position: absolute;
  box-sizing: border-box;
  overflow: hidden;
  background: #8abcfa;
  color: #000;
  font-family: AlibabaPuHuiTi_3_65_Medium, 'Alibaba PuHuiTi 3.0', PingFangSC-Regular, Source Han Sans CN, sans-serif;
  font-synthesis: none;
  font-weight: 500;
  letter-spacing: 0;
  -webkit-font-smoothing: antialiased;
  text-rendering: geometricprecision;
}

.about-story-card__logo,
.about-story-card__copy {
  position: absolute;
}

.about-story-card__logo {
  display: block;
  object-fit: contain;
  user-select: none;
}

.about-story-card__copy {
  margin: 0;
  overflow-wrap: break-word;
}
</style>
